import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import type { MessageCreate } from '@/types/project_messages';

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetches all messages for a project (oldest first).
 *
 * @param projectId - Project ID to fetch messages for
 * @returns Query result with messages array and total count
 *
 * @example
 * const { data, isLoading } = useProjectMessages(projectId);
 * const messages = data?.messages ?? [];
 */
export function useProjectMessages(projectId: string | undefined) {
  return useQuery({
    queryKey: ['project-messages', projectId],
    queryFn: async () => {
      const response = await api.projectMessages.list(projectId!);
      return response;
    },
    enabled: !!projectId,
    staleTime: 10_000,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Sends a new project message.
 *
 * Cache invalidation: Invalidates all message queries for the project.
 *
 * @example
 * const sendMessage = useSendProjectMessage();
 * await sendMessage.mutateAsync({ projectId, data: { content: 'Hello', is_internal: false } });
 */
export function useSendProjectMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: { projectId: string; data: MessageCreate }) =>
      api.projectMessages.create(vars.projectId, vars.data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['project-messages', variables.projectId] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to send message');
    },
  });
}

/**
 * Deletes a project message.
 *
 * @example
 * const deleteMessage = useDeleteProjectMessage();
 * await deleteMessage.mutateAsync({ projectId, messageId });
 */
export function useDeleteProjectMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: { projectId: string; messageId: string }) =>
      api.projectMessages.delete(vars.projectId, vars.messageId),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['project-messages', variables.projectId] });
      toast.success('Message deleted');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete message');
    },
  });
}

/**
 * Marks a message as read by the current user.
 *
 * @example
 * const markRead = useMarkMessageRead();
 * await markRead.mutateAsync({ projectId, messageId });
 */
export function useMarkMessageRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: { projectId: string; messageId: string }) =>
      api.projectMessages.markRead(vars.projectId, vars.messageId),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['project-messages', variables.projectId] });
    },
    onError: () => {
      // Silent — read tracking is non-critical
    },
  });
}
